import axios from "axios";

import { apiConfig } from "../config/authConfig";
import { CredentialManager } from "../classes/credentialManager";
import { IShipment_DeletedSearchRequest, ILoad_DeletedLight, IShipment_Deleted, IDocument_Deleted } from "../types/deleted";
import moment from "moment";

export namespace DeletedService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Searches deleted shipments by provided criteria.
	 * @param request The search request criteria.
	 **********/
	export async function searchDeletedShipments(request: IShipment_DeletedSearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/deleted/shipment/search",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Convert one api response object to one deleted Shipment.
	 * @param data Shipment response data from an API.
	 **********/
	export function mapShipment(data: any) {
		let shipment: IShipment_Deleted = {
			id: data.id,
			bolNumber: data.hwiNumber,
			manifestNumbers: data.manifestNumbers,
			customerId: data.customerId,
			customerName: data.customerName,
			customerClosedThroughDate: data.customerClosedThroughDate,
			vendorId: data.vendorId,
			vendorName: data.vendorName,
			transporterId: data.transporterId,
			transporterName: data.transporterName,
			plantWeightLbs: data.plantWeight,
			vendorWeightLbs: data.vendorWeight,
			pickupDate: moment(data.pickupDate).toDate(),
			scheduledPickupDate: data.scheduledPickupDate ? moment(data.scheduledPickupDate).toDate() : undefined,
			status: data.status,
			notes: data.notes,
			materials: data.materials,
			expenses: data.expenses,
			accountingDetails: data.accountingDetails,
			documents: mapDocuments(data.documents),
			isLegacy: data.isLegacy,
			vendorLoadId: data.vendorLoadId,
			isRedwood: data.isRedwood,
			batchId: data.batchId,
			batchStatus: data.batchStatus,
			isBatched: data.isBatched,
			deletedDate: data.deletedDate,
			deletedBy: data.deletedBy,
		}

		return shipment;
	}

	/**********
	 * Convert an array of api response objects to an array of deleted Shipments.
	 * @param data Shipment data from an API response.
	 **********/
	export function mapShipments(data: any) {
		let shipments: IShipment_Deleted[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				shipments.push(mapShipment(item));
			})
		}

		return shipments;
	}

	/**********
	 * Gets all deleted loads.
	 * @param start The earilest arrival date.
	 * @param customers The list of customer IDs to filter by.
	 **********/
	export async function getDeletedLoads(start: Date, customers: number[]) {
		const date = String(start.getFullYear()) + String(start.getMonth() + 1).padStart(2, '0') + String(start.getDate()).padStart(2, '0');

		const apiResponse = axios.get(
			apiConfig.default + "/deleted/load/all/" + date + "/" + customers.toString(),
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Convert one api response object to one deleted Load.
	 * @param data Load response data from an API.
	 **********/
	export function mapLoadLight(data: any) {
		let load: ILoad_DeletedLight = {
			id: data.id,
			hwiNumber: data.hwiNumber,
			hwiShortNumber: data.hwiNumber ? data.hwiNumber.replace("HWI-", "") : "",
			manifestNumbers: data.manifestNumbers,
			customerName: data.customerName,
			vendorName: data.vendorName,
			trailerNumber: data.trailerNumber,
			plantWeight: data.plantWeight,
			vendorWeight: data.vendorWeight,
			scheduledPickupDate: data.scheduledPickupDate,
			pickupDate: data.pickupDate,
			status: data.status,
			loadDataSentToVendor: data.loadDataSentToVendor,
			deletedDate: data.deletedDate,
			deletedBy: data.deletedBy,
		}

		return load;
	}

	/**********
	 * Convert an array of api response objects to an array of deleted Loads.
	 * @param data Load data from an API response.
	 **********/
	export function mapLoadLightList(data: any) {
		let loads: ILoad_DeletedLight[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				loads.push(mapLoadLight(item));
			})
		}

		return loads;
	}

	/**********
	 * Convert one api response object to one deleted Document.
	 * @param data Document response data from an API.
	 **********/
	export function mapDocument(data: any) {
		let document: IDocument_Deleted = {
			id: data.id,
			contentType: data.contentType,
			documentTypeId: data.documentTypeId,
			documentTypeName: data.documentTypeName,
			entityId: data.entityId,
			entityName: data.entityName,
			entityTypeId: data.entityTypeId,
			entityTypeName: data.entityTypeName,
			expirationDate: data.expirationDate,
			file: data.file,
			location: data.location,
			name: data.name,
			uploadDate: data.uploadDate,
			status: data.expirationDate ? (moment(data.expirationDate).diff(Date.now()) >= 0 ? "Active" : "Expired") : "Active",
			isSensitive: data.isSensitive,
			deletedDate: data.deletedDate,
			deletedBy: data.deletedBy,
		}

		return document;
	}

	/**********
	 * Convert an array of api response objects to an array of deleted Documents.
	 * @param data Document data from an API response.
	 **********/
	export function mapDocuments(data: any) {
		let documents: IDocument_Deleted[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				documents.push(mapDocument(item));
			})
		}

		return documents;
	}

	/**********
	 * Restores a soft-deleted shipment.
	 * @param shipmentId The ID of the shipment to restore.
	 **********/
	export async function softRestoreDeletedShipment(shipmentId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/deleted/shipment/restore/" + shipmentId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Restores a soft-deleted load.
	 * @param loadId The ID of the load to restore.
	 **********/
	export async function softRestoreDeletedLoad(loadId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/deleted/load/restore/" + loadId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}